import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { Menu, X } from 'lucide-react';

export const Navbar = () => {
  const { t, language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const languages = [
    { code: 'it', label: 'IT' },
    { code: 'en', label: 'EN' },
    { code: 'fr', label: 'FR' },
    { code: 'es', label: 'ES' }
  ];

  const navLinks = [
    { path: '/', label: t.nav.home },
    { path: '/per-pizzaioli', label: t.nav.forChefs },
    { path: '/per-ristoratori', label: t.nav.forRestaurants },
    { path: '/chi-siamo', label: t.nav.about }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-primary/10" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-10 h-10 rounded-sm bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-xl font-serif">A</span>
            </div>
            <span className="text-foreground font-serif text-xl tracking-tight hidden sm:block">
              Artisan Pizza <span className="text-primary">Talent</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Language Selector & Admin */}
          <div className="hidden md:flex items-center gap-4">
            <div className="flex items-center border border-primary/20 rounded-sm overflow-hidden" data-testid="language-selector">
              {languages.map((lang, idx) => (
                <React.Fragment key={lang.code}>
                  <button
                    onClick={() => setLanguage(lang.code)}
                    className={`lang-btn ${language === lang.code ? 'active' : ''}`}
                    data-testid={`lang-btn-${lang.code}`}
                  >
                    {lang.label}
                  </button>
                  {idx < languages.length - 1 && (
                    <span className="text-primary/30">|</span>
                  )}
                </React.Fragment>
              ))}
            </div>
            <Link
              to="/admin"
              className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors duration-200"
              data-testid="admin-link"
            >
              {t.nav.admin}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-foreground"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden py-6 border-t border-primary/10 animate-fade-in" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`text-base font-medium py-2 ${
                    isActive(link.path) ? 'text-primary' : 'text-muted-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/admin"
                onClick={() => setIsOpen(false)}
                className="text-base font-medium py-2 text-muted-foreground"
              >
                {t.nav.admin}
              </Link>
              <div className="flex items-center gap-2 pt-4 border-t border-primary/10">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`px-3 py-1 text-sm rounded-sm ${
                      language === lang.code
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
